import { LIGHTNING_STRIKE_COLOR, LIGHTNING_STRIKE_INNER_COLOR } from './colors';
import type { LightningGroup } from './types';

/**
 * SPAWN 5, authored in the lightning lab and pasted back. Started life as a
 * duplicate of Spawn 3 -- the lab exported it as "Spawn 3 copy" under a raw
 * UUID -- and has since been moved, re-aimed and re-tracked into its own
 * strike, low and rolled almost upside down (182deg).
 *
 * THE GROUP ID WAS RENAMED from that UUID to `spawn-5`, which is safe:
 * unlike LINE ids, a group id never seeds a shape. It only keys the
 * scheduler's per-cycle offset and seed maps, the React list and the lab's
 * selection. The LINE ids below are untouched for exactly that reason.
 *
 * NOTE THE SHARED LINE ID. `fef3a9b9-...` also exists in ./spawn-3, so both
 * bolts derive the same `lineSeed`. They do not move in lockstep, because
 * the scheduler hands each GROUP its own freshly rolled seed every cycle and
 * Bolt adds the two together -- but renaming one and not the other would
 * reshape it, so leave the pair alone.
 *
 * Runs 1.45s end to end, the longest of the four, so this group is what
 * sizes the shared cycle.
 */
export const LIGHTNING_SPAWN_5_GROUP: LightningGroup = {
  id: 'spawn-5',
  name: 'Spawn 5',
  enabled: true,
  position: [0.17, -0.28, 0],
  rotationDeg: [31, 6, 182],
  scale: 2,
  lines: [
    // AN EMPTY TRACK, not a missing one -- and the two behave identically.
    // sampleLightningTrack returns a flat intensity 1 for both, and
    // lineEnvelope reads `keyframes?.length` as falsy and so applies the
    // long legacy fade instead of the short 0.045 one. The result is a
    // single steady strand that swells and fades over its full 1s, with no
    // flicker of its own: the quiet one the others are cut against.
    {
      id: 'fef3a9b9-0b6d-4dc9-b88b-ffe4ee92af63',
      enabled: true,
      start: [0.09, -0.192, -0.01],
      end: [0.59, -0.012, 0.46],
      delay: 0.45,
      lifetime: 1,
      revealSeconds: 0.07,
      brightness: 5.2,
      thickness: 0.00126,
      jag: 0.01,
      detail: 20,
      flickerHz: 16,
      color: LIGHTNING_STRIKE_COLOR,
      innerColor: LIGHTNING_STRIKE_INNER_COLOR,
      keyframes: [],
    },
    // The counterpart, and the one group member that is BACKWARDS: it opens
    // weak, dies at 0.186, and only reaches full at 0.418 -- so its strike
    // lands late, after the other three groups have already fired.
    {
      id: '5e6f8c4b-3491-4466-b5de-40cb420f1e26',
      enabled: true,
      start: [0.21, -0.062, -0.01],
      end: [0.57, -0.082, 0.37],
      delay: 0.45,
      lifetime: 0.92,
      revealSeconds: 0.07,
      brightness: 5.2,
      thickness: 0.00126,
      jag: 0.01,
      detail: 20,
      flickerHz: 16,
      color: LIGHTNING_STRIKE_COLOR,
      innerColor: LIGHTNING_STRIKE_INNER_COLOR,
      keyframes: [
        { time: 0, intensity: 0.5, endOffset: [0, 0, 0] },
        { time: 0.15785714285714286, intensity: 0.2, endOffset: [0, 0.07, 0] },
        { time: 0.18571428571428572, intensity: 0, endOffset: [0, 0.09, 0] },
        { time: 0.4178571428571428, intensity: 1, endOffset: [0, 0.015, 0] },
        { time: 0.5, intensity: 1, endOffset: [0, 0.025, 0] },
        { time: 0.98, intensity: 0.4, endOffset: [0, 0.025, 0] },
      ],
    },
  ],
};
